import {
    selectActiveWorkspace,
    selectIsCreatingProject,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectUserFirstName } from "features/entity-caching/members/UserManagementSlice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { ScrollableArea } from "@nova/component-scrollable-area"
import classNames from "classnames"
import { WorkspaceHomeBodyReturningUser } from "./homes/WorkspaceHomeBodyReturningUser"
import { WorkspaceHomeBodyNewUser } from "./homes/WorkspaceHomeBodyNewUser"
import { useEffect, useState } from "react"
import { ButtonPrimary, ButtonSecondary, ButtonTertiary } from "@nova/component-btn"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { WorkspaceTreeMaxDepth } from "features/data-management/workspace/constants"
import * as Icons from "@nova/component-icons"
import { openWorkspaceShareModal } from "features/data-management/workspace/context/thunks"
import {
    useActiveFolder,
    useCreateFolder,
    useCreateProject,
    useCreateTagChain,
} from "features/data-management/workspace/context/navigation"

export const WorkspaceHome = () => {
    const dispatch = useAppDispatch()
    const workspaceName = useAppSelector(selectActiveWorkspace())?.name
    const firstName = useAppSelector(selectUserFirstName)
    const isFirstLogin = sessionStorage.getItem("isFirstLogin") === "true"

    // Allows the welcome animation to happen just once.
    const [welcomeAnimationIsDone, setWelcomeAnimationIsDone] = useState(false)
    useEffect(() => {
        const timeout = setTimeout(() => setWelcomeAnimationIsDone(true), 1000)
        return () => {
            clearTimeout(timeout)
        }
    }, [])

    const activeFolder = useActiveFolder()
    const { isEditor } = useWorkspacePermissions(activeFolder?.id)
    const createFolder = useCreateFolder()
    const createProject = useCreateProject()
    const createTagChain = useCreateTagChain()
    const tagChain = createTagChain(activeFolder?.id) ?? []
    const canCreateFolder = isEditor && tagChain.length <= WorkspaceTreeMaxDepth

    const isCreatingProject = useAppSelector(selectIsCreatingProject())

    return (
        <ScrollableArea shouldPreventScrollbarShifting={true} className="h-full">
            <div className="w-full flex justify-center">
                <div className="px-12 py-8 w-full max-w-[1160px]">
                    <div className="max-w-[90rem] mx-auto">
                        <small className="text-gray-300 xs select-none">
                            {workspaceName || "Workspace"}
                        </small>
                        <div className="flex gap-4 items-center">
                            <div
                                className={classNames("w-full overflow-hidden", {
                                    "ease-out transition-[width] duration-1000":
                                        !welcomeAnimationIsDone,
                                })}
                            >
                                <h1 className="text-gray-0 text-[4rem] select-none">{`Welcome${!isFirstLogin ? " back" : ""}, ${firstName}.`}</h1>
                            </div>
                            <div className="flex gap-2 items-center">
                                <ButtonTertiary
                                    onClick={() =>
                                        dispatch(openWorkspaceShareModal(activeFolder?.id))
                                    }
                                    title="Invite"
                                />
                                {canCreateFolder && (
                                    <ButtonSecondary
                                        onClick={() =>
                                            createFolder({ insideOfId: activeFolder?.id })
                                        }
                                        LeftIcon={Icons.CreateFolder}
                                        title="Folder"
                                    />
                                )}
                                {isEditor && (
                                    <ButtonPrimary
                                        onClick={() => createProject()}
                                        isLoading={isCreatingProject}
                                        LeftIcon={Icons.Plus}
                                        title="Project"
                                    />
                                )}
                            </div>
                        </div>

                        {!isFirstLogin ? (
                            <WorkspaceHomeBodyReturningUser />
                        ) : (
                            <WorkspaceHomeBodyNewUser />
                        )}
                    </div>
                </div>
            </div>
        </ScrollableArea>
    )
}
